import { useLazyQuery } from '@apollo/client';
import { useDealPromptFeatureFlaggedFilters } from '@openx/components-deal-prompt/lib/hooks/useDealPromptFeatureFlaggedFilters.ts';
import { buildSearchQueryInput } from '@openx/components-deal-prompt/lib/utils/buildSearchQueryInput.ts';
import type {
  DealPromptSearchFetchArgs,
  UseDealPromptAppsSearchFetch,
} from '@openx/components-deal-prompt/lib/utils/context/type.ts';
import { mapCriteriaToFiltersBody } from '@openx/components-deal-prompt/lib/utils/filters/filterMappers.ts';
import { type DealPromptFilters, dealPromptContextTypes } from '@openx/components-deal-prompt/lib/utils/types.ts';
import type { AllTableCriteria } from '@openx/types/tableCriteria.ts';
import { useCallback } from 'react';
import { InventoryBundleSearchDocument } from '../../../graphql/query/dealPrompt/GetInventoryBundleSearch.generated.tsx';

export const useDealPromptAppsSearchFetch = (
  criteria: AllTableCriteria<DealPromptFilters>
): UseDealPromptAppsSearchFetch => {
  const [fetch] = useLazyQuery(InventoryBundleSearchDocument, {
    // TODO: EXCH-14467 remove this caching once duplicte id change is merged
    fetchPolicy: 'no-cache',
  });
  const featureFlaggedFilters = useDealPromptFeatureFlaggedFilters();

  const fetchDealPromptApps = useCallback(
    async ({ query, queries }: DealPromptSearchFetchArgs, abortSignal?: AbortSignal) => {
      const input = {
        query: buildSearchQueryInput({ query, queries }),
        sortBy: 'totalImpressions',
        view: 'desc',
        excludeQuery: [],
        filters: mapCriteriaToFiltersBody(criteria, dealPromptContextTypes.APPS, featureFlaggedFilters),
      };

      const result = await fetch({
        variables: {
          input,
        },
        context: {
          fetchOptions: {
            signal: abortSignal,
          },
        },
      });

      return {
        cumulativeData: result.data?.inventoryBundleSearch.body.cumulativeData ?? [],
        numberOfResults: result?.data?.inventoryBundleSearch.body.totalRecords ?? 0,
        _error: result?.error,
      };
    },
    [fetch, criteria, featureFlaggedFilters]
  );

  return {
    fetchDealPromptApps,
  };
};
